import { type SCChecklist } from '@kalos/kalos-rpc';
import { type FC } from 'react';

import { type ChecklistDraft, type ChecklistGate, isFreeTextQuestion } from './useScChecklist';

type Props = {
  checklists: SCChecklist[];
  draft: ChecklistDraft;
  gate: ChecklistGate;
  saving: boolean;
  disabled?: boolean;
  setAnswer: (questionId: number, next: { optionIds: number[]; freeText: string }) => void;
  onSave: () => void;
};

/**
 * The ServiceChannel compliance checklist for a job.
 *
 * Renders nothing at all for jobs without one, which is most jobs — the panel
 * is mounted unconditionally by the job view and disappears on its own rather
 * than the caller having to know whether a job is a ServiceChannel job.
 */
export const ScChecklist: FC<Props> = ({
  checklists,
  draft,
  gate,
  saving,
  disabled,
  setAnswer,
  onSave,
}) => {
  if (gate.notApplicable) return null;

  // A failed load is shown rather than hidden: completion is blocked while it
  // is failing, so the tech needs to know why and that retrying is the fix.
  if (gate.failed) {
    return (
      <div style={{ padding: 12, border: '1px solid #d32f2f', borderRadius: 4 }}>
        <strong>ServiceChannel checklist could not be loaded.</strong>
        <div>
          This job cannot be completed until the checklist loads. Reload the page to try again.
        </div>
      </div>
    );
  }

  if (!gate.loaded) return <div style={{ padding: 12 }}>Loading checklist…</div>;

  return (
    <div style={{ padding: 12 }}>
      {checklists.map((list) => (
        <div key={list.id} style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 8 }}>{list.name}</h3>
          {[...list.questions]
            .sort((a, b) => a.sequence - b.sequence)
            .map((q) => {
              const entry = draft[q.id] ?? { optionIds: [], freeText: '' };
              const isFreeText = isFreeTextQuestion(q);
              return (
                <div key={q.id} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontWeight: 500 }}>
                    {q.question}
                    {q.answerRequired ? ' *' : ''}
                  </label>

                  {isFreeText ? (
                    <input
                      type="text"
                      value={entry.freeText}
                      disabled={disabled || saving}
                      onChange={(e) => setAnswer(q.id, { ...entry, freeText: e.target.value })}
                      style={{ width: '100%', padding: 6 }}
                    />
                  ) : (
                    [...q.options]
                      .sort((a, b) => a.sequence - b.sequence)
                      .map((o) => {
                        const checked = entry.optionIds.includes(o.id);
                        return (
                          <div key={o.id}>
                            <label>
                              <input
                                // Single-select must behave as a radio or a tech
                                // can silently submit two answers to a question
                                // ServiceChannel accepts only one answer for.
                                type={q.allowMultiSelect ? 'checkbox' : 'radio'}
                                name={`sc-q-${q.id}`}
                                checked={checked}
                                disabled={disabled || saving}
                                onChange={() => {
                                  const optionIds = q.allowMultiSelect
                                    ? checked
                                      ? entry.optionIds.filter((id) => id !== o.id)
                                      : [...entry.optionIds, o.id]
                                    : [o.id];
                                  setAnswer(q.id, { ...entry, optionIds });
                                }}
                              />{' '}
                              {o.answer}
                              {o.responseResultInFailure ? (
                                <span style={{ color: '#d32f2f' }}> (fails the checklist)</span>
                              ) : null}
                            </label>
                          </div>
                        );
                      })
                  )}
                </div>
              );
            })}
        </div>
      ))}

      {gate.unanswered.length > 0 ? (
        <div style={{ color: '#d32f2f', marginBottom: 8 }}>
          {gate.unanswered.length} required question
          {gate.unanswered.length === 1 ? '' : 's'} still unanswered. The job cannot be completed
          until they are.
        </div>
      ) : null}

      <button type="button" onClick={onSave} disabled={disabled || saving}>
        {saving ? 'Saving…' : 'Save checklist'}
      </button>
    </div>
  );
};

export default ScChecklist;
